import { computed, observable, runInAction } from "mobx"

import { getLineGutterWidth } from "@/lib/diff/codeRenderingCommon.ts"
import { Model } from "@/lib/Model.ts"

import type { DiffFile, DiffViewModel } from "@/components/DiffView/DiffView.tsx"
import type { GitCommitChange } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GlobalContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { Property } from "csstype"


export abstract class BaseDiffViewModel extends Model implements DiffViewModel {
  readonly files = observable.box<DiffFile[]>([])
  readonly error = observable.box<Error | null>(null)

  protected readonly changesLoaded = observable.box(false)

  protected abstract readonly model: TaskViewModel
  protected abstract readonly context: GlobalContext
  protected abstract readonly queries: Queries

  readonly guttersWidth = computed(() => {
    const widths = new Set<Property.Width>

    for (const item of this.files.get()) {
      if (item.diff) {
        widths.add(getLineGutterWidth(item.diff))
      }
    }

    return `max(${[...widths].join(", ")})`
  })

  async loadChanges(loadDiff: boolean): Promise<void> {
    try {
      if (!this.changesLoaded.get()) {
        runInAction(() => {
          this.isLoading.set(true)
          this.context.isLoading.set(true)
        })
      }

      const changes = await this.fetchChanges() || []
      const files = await Promise.all(changes.map(it => this.fetchDiff(it, loadDiff)))

      runInAction(() => {
        this.files.set(files)
        this.error.set(null)
      })
    } catch (err) {
      runInAction(() => {
        this.files.set([])
        this.error.set(err instanceof Error ? err : new Error(String(err)))
      })
    } finally {
      runInAction(() => {
        this.isLoading.set(false)
        this.context.isLoading.set(false)
        this.changesLoaded.set(true)
      })
    }
  }

  protected async fetchDiff(file: GitCommitChange, loadDiff: boolean): Promise<DiffFile> {
    if (!loadDiff) return { file, diff: null }

    const diff = await this.queries.getInlineDiff(this.model.project.get().key, this.model.repositoryName, file)

    return { file, diff }
  }

  abstract downloadPatch(): void

  protected abstract fetchChanges(): Promise<GitCommitChange[]>
}
